import { API_ERROR_CODES, API_PREFIX, IDEMPOTENCY_HEADER, PERM_VERSION_HEADER, REQUEST_ID_HEADER, } from '@concours/shared';
/**
 * Client HTTP commun aux deux portails.
 * - préfixe `/api/v1`, JSON, token Bearer (ADR-014, token gardé en mémoire) ;
 * - erreurs converties en `ApiRequestError` (format uniforme de l'API) ;
 * - `idempotent: true` → génère et envoie un `Idempotency-Key` ;
 * - callbacks 401 (déconnexion) et changement de `x-perm-version` (rechargement RBAC).
 */
export class ApiRequestError extends Error {
    status;
    code;
    details;
    requestId;
    constructor(status, code, message, details, requestId) {
        super(message);
        this.status = status;
        this.code = code;
        this.details = details;
        this.requestId = requestId;
        this.name = 'ApiRequestError';
    }
    get isForbidden() {
        return this.status === 403;
    }
    get isUnauthenticated() {
        return this.status === 401;
    }
    get isValidation() {
        return this.code === API_ERROR_CODES.VALIDATION;
    }
    /** Erreurs de validation par champ (premier message de chaque champ). */
    get fieldErrors() {
        const result = {};
        if (!Array.isArray(this.details))
            return result;
        for (const item of this.details) {
            if (item.field && item.message && !result[item.field])
                result[item.field] = item.message;
        }
        return result;
    }
}
export function filenameFrom(disposition) {
    if (!disposition)
        return null;
    const star = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (star)
        return decodeURIComponent(star[1]);
    return disposition.match(/filename="?([^";]+)"?/i)?.[1] ?? null;
}
/** Envoi avec progression : XMLHttpRequest converti en `Response` standard. */
function xhrFetch(url, init, onProgress) {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open(init.method, url);
        for (const [key, value] of Object.entries(init.headers))
            xhr.setRequestHeader(key, value);
        xhr.responseType = 'arraybuffer';
        xhr.upload.addEventListener('progress', (event) => {
            if (event.lengthComputable && event.total > 0)
                onProgress(event.loaded / event.total);
        });
        xhr.addEventListener('load', () => {
            const headers = new Headers();
            for (const line of xhr.getAllResponseHeaders().trim().split(/\r?\n/)) {
                const at = line.indexOf(':');
                if (at > 0)
                    headers.append(line.slice(0, at).trim(), line.slice(at + 1).trim());
            }
            onProgress(1);
            const empty = xhr.status === 204 || xhr.status === 205 || xhr.status === 304;
            resolve(new Response(empty ? null : xhr.response, {
                status: xhr.status,
                statusText: xhr.statusText,
                headers,
            }));
        });
        xhr.addEventListener('error', () => reject(new TypeError('Erreur réseau')));
        xhr.addEventListener('abort', () => reject(new DOMException('Envoi interrompu', 'AbortError')));
        init.signal?.addEventListener('abort', () => xhr.abort(), { once: true });
        xhr.send(init.body);
    });
}
function newKey() {
    return globalThis.crypto.randomUUID();
}
export function createApiClient(options = {}) {
    const doFetch = options.fetch ?? globalThis.fetch.bind(globalThis);
    const base = (options.baseUrl ?? '').replace(/\/$/, '');
    async function request(method, path, opts = {}) {
        const url = new URL(`${base}${opts.raw ? '' : API_PREFIX}${path}`, base || globalThis.location?.origin || 'http://localhost');
        for (const [key, value] of Object.entries(opts.query ?? {})) {
            if (value !== undefined && value !== null)
                url.searchParams.set(key, String(value));
        }
        const headers = { accept: 'application/json', ...opts.headers };
        const token = options.getToken?.();
        if (token)
            headers.authorization = `Bearer ${token}`;
        if (opts.idempotent) {
            headers[IDEMPOTENCY_HEADER] = typeof opts.idempotent === 'string' ? opts.idempotent : newKey();
        }
        let body;
        if (opts.body instanceof FormData) {
            body = opts.body;
        }
        else if (opts.body !== undefined) {
            headers['content-type'] = 'application/json';
            body = JSON.stringify(opts.body);
        }
        const init = { method, headers, body, signal: opts.signal };
        const response = opts.onUploadProgress && body instanceof FormData && typeof XMLHttpRequest !== 'undefined'
            ? await xhrFetch(url.toString(), init, opts.onUploadProgress)
            : await doFetch(url.toString(), init);
        const permVersion = response.headers.get(PERM_VERSION_HEADER);
        if (permVersion)
            options.onPermVersion?.(Number(permVersion));
        if (opts.responseType === 'blob' && response.ok) {
            const result = {
                blob: await response.blob(),
                filename: filenameFrom(response.headers.get('content-disposition')),
            };
            return result;
        }
        const text = await response.text();
        const data = text ? safeJson(text) : null;
        if (!response.ok) {
            const payload = data?.error;
            const error = new ApiRequestError(response.status, payload?.code ?? `E_HTTP_${response.status}`, payload?.message ?? response.statusText ?? 'Erreur réseau', payload?.details, payload?.requestId ?? response.headers.get(REQUEST_ID_HEADER) ?? undefined);
            if (response.status === 401)
                options.onUnauthenticated?.(error);
            throw error;
        }
        return data;
    }
    return {
        request,
        get: (path, opts) => request('GET', path, opts),
        post: (path, body, opts) => request('POST', path, { ...opts, body }),
        put: (path, body, opts) => request('PUT', path, { ...opts, body }),
        patch: (path, body, opts) => request('PATCH', path, { ...opts, body }),
        delete: (path, opts) => request('DELETE', path, opts),
        download: (path, opts) => request('GET', path, { ...opts, responseType: 'blob' }),
    };
}
function safeJson(text) {
    try {
        return JSON.parse(text);
    }
    catch {
        return { error: { code: 'E_INVALID_JSON', message: text.slice(0, 200) } };
    }
}
export * from './session.js';
//# sourceMappingURL=client.js.map